import { clsx } from 'clsx'
import { type ReactNode } from 'react'

type HeadingProps = {
  as?: 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  dark?: boolean
} & React.ComponentPropsWithoutRef<
  'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
>

export function Heading({
  className,
  as: Element = 'h2',
  dark = false,
  ...props
}: HeadingProps) {
  return (
    <Element
      {...props}
      data-dark={dark ? 'true' : undefined}
      className={clsx(
        className,
        'text-4xl font-medium tracking-tighter text-pretty text-gray-950 data-dark:text-white sm:text-6xl',
      )}
    />
  )
}

export function Subheading({
  className,
  as: Element = 'h2',
  dark = false,
  ...props
}: HeadingProps) {
  return (
    <Element
      {...props}
      data-dark={dark ? 'true' : undefined}
      className={clsx(
        className,
        'font-mono text-xs/5 font-semibold tracking-widest text-gray-500 uppercase data-dark:text-gray-400',
      )}
    />
  )
}

export function Lead({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'p'>) {
  return (
    <p
      className={clsx(
        'text-2xl font-medium',
        !hasTextColor(className) && 'text-gray-500',
        className,
      )}
      {...props}
    />
  )
}

type TextProps = {
  className?: string
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'p'
    | 'lead'
    | 'small'
    | 'eyebrow'
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div'
  children: ReactNode
} & React.ComponentPropsWithoutRef<'div'>

function hasTextColor(className?: string): boolean {
  if (!className) return false
  return /\btext-(slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose|primary|secondary)-\d+\b/.test(
    className,
  )
}

export function Text({ className, variant = 'p', as, children }: TextProps) {
  const Component =
    as ?? ((variant.startsWith('h') ? variant : 'p') as React.ElementType)

  const hasCustomTextColor = hasTextColor(className)

  const variantStyles = {
    h1: 'text-4xl font-bold tracking-tight',
    h2: 'text-3xl font-bold tracking-tight',
    h3: 'text-xl font-semibold',
    h4: 'text-lg font-semibold',
    h5: 'text-base font-semibold',
    h6: 'text-sm font-semibold',
    p: 'text-base',
    lead: 'text-lg',
    small: 'text-sm',
    eyebrow:
      'font-mono text-xs/5 font-semibold tracking-widest uppercase data-dark:text-gray-400',
  }

  const variantColors = {
    h1: 'text-gray-900',
    h2: 'text-gray-900',
    h3: 'text-gray-900',
    h4: 'text-gray-900',
    h5: 'text-gray-900',
    h6: 'text-gray-900',
    p: 'text-gray-600',
    lead: 'text-gray-600',
    small: 'text-gray-500',
    eyebrow: 'text-gray-500',
  }

  return (
    <Component
      className={clsx(
        variantStyles[variant as keyof typeof variantStyles],
        !hasCustomTextColor &&
          variantColors[variant as keyof typeof variantColors],
        className,
      )}
    >
      {children}
    </Component>
  )
}
